import type { BreadcrumbItem } from '@/components/ui/PanelBreadcrumbs'
import type { PanelContent } from '@/components/ui/PanelContent'
import { fetchFileContent } from '@/utils/fileUtils'
import { useCallback, useState } from 'react'

interface FileData {
  name: string
  url?: string
  downloadUrl?: string
  mimeType?: string
  size?: number
  id?: string
}

interface UseFilePanelOptions {
  onOpenPanel?: (content: PanelContent, breadcrumbs: BreadcrumbItem[]) => void
  parentBreadcrumbs?: BreadcrumbItem[]
}

/**
 * Check if a file is an image based on its extension
 */
function isImageFile(fileName: string): boolean {
  const extension = fileName.toLowerCase().split('.').pop()
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico']
  return imageExtensions.includes(extension || '')
}

/**
 * Check if a file is a spreadsheet (CSV, Excel, etc.)
 */
function isSpreadsheetFile(fileName: string, mimeType?: string): boolean {
  const extension = fileName.toLowerCase().split('.').pop()
  const spreadsheetExtensions = ['csv', 'tsv', 'xls', 'xlsx', 'ods', 'numbers']

  return (
    spreadsheetExtensions.includes(extension || '') ||
    (mimeType?.includes('spreadsheet') ?? false) ||
    (mimeType?.includes('excel') ?? false) ||
    (mimeType?.includes('text/csv') ?? false) ||
    (mimeType?.includes('text/tab-separated-values') ?? false)
  )
}

/**
 * Check if a file is a document that can be previewed with Google Docs Viewer
 * Supports: Office docs, PDFs, text files, and more
 */
function isPreviewableDocument(fileName: string, mimeType?: string): boolean {
  const extension = fileName.toLowerCase().split('.').pop()
  const previewableExtensions = [
    // Microsoft Office
    'doc',
    'docx',
    'ppt',
    'pptx',
    // PDF
    'pdf',
    // Text formats
    'txt',
    'rtf',
    // OpenDocument formats
    'odt',
    'odp',
    // Other document formats
    'pages',
    'key', // Apple iWork
    'eps',
    'ps', // PostScript
  ]

  return (
    previewableExtensions.includes(extension || '') ||
    (mimeType?.includes('document') ?? false) ||
    (mimeType?.includes('word') ?? false) ||
    (mimeType?.includes('presentation') ?? false) ||
    (mimeType?.includes('powerpoint') ?? false) ||
    (mimeType?.includes('pdf') ?? false) ||
    (mimeType?.includes('text/plain') ?? false) ||
    (mimeType?.includes('application/rtf') ?? false)
  )
}

export function useFilePanel(options: UseFilePanelOptions = {}) {
  const { onOpenPanel, parentBreadcrumbs = [] } = options
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openFileInPanel = useCallback(
    async (file: FileData) => {
      if (!onOpenPanel) {
        console.warn('useFilePanel: onOpenPanel callback not provided')
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Check if this is an image file
        const isImage = file.mimeType?.startsWith('image/') || isImageFile(file.name)
        // Check if this is a spreadsheet file
        const isSpreadsheet = isSpreadsheetFile(file.name, file.mimeType)
        // Check if this is a previewable document
        const isDocument = isPreviewableDocument(file.name, file.mimeType)

        if (isImage && (file.url || file.downloadUrl)) {
          // For images, create image content instead of fetching as text
          const panelContent: PanelContent = {
            type: 'image',
            fileName: file.name,
            imageUrl: file.url || file.downloadUrl!,
            downloadUrl: file.downloadUrl || file.url,
            mimeType: file.mimeType,
          }

          // Create breadcrumbs
          const breadcrumbs: BreadcrumbItem[] = [
            ...parentBreadcrumbs,
            {
              label: file.name,
              id: file.id || file.name,
              clickable: false, // Current item is not clickable
            },
          ]

          // Open in panel
          onOpenPanel(panelContent, breadcrumbs)
          return
        }

        if (isSpreadsheet && (file.url || file.downloadUrl)) {
          // For spreadsheets, create spreadsheet content with table preview
          const panelContent: PanelContent = {
            type: 'spreadsheet',
            fileName: file.name,
            spreadsheetUrl: file.url || file.downloadUrl!,
            downloadUrl: file.downloadUrl || file.url,
            mimeType: file.mimeType,
          }

          // Create breadcrumbs
          const breadcrumbs: BreadcrumbItem[] = [
            ...parentBreadcrumbs,
            {
              label: file.name,
              id: file.id || file.name,
              clickable: false, // Current item is not clickable
            },
          ]

          // Open in panel
          onOpenPanel(panelContent, breadcrumbs)
          return
        }

        if (isDocument && (file.url || file.downloadUrl)) {
          // For previewable documents, create document content with Google Docs viewer
          const panelContent: PanelContent = {
            type: 'document',
            fileName: file.name,
            documentUrl: file.url || file.downloadUrl!,
            downloadUrl: file.downloadUrl || file.url,
            mimeType: file.mimeType,
          }

          // Create breadcrumbs
          const breadcrumbs: BreadcrumbItem[] = [
            ...parentBreadcrumbs,
            {
              label: file.name,
              id: file.id || file.name,
              clickable: false, // Current item is not clickable
            },
          ]

          // Open in panel
          onOpenPanel(panelContent, breadcrumbs)
          return
        }

        // Fetch file content for non-image files
        const fileContent = await fetchFileContent(file)

        // Create panel content
        const panelContent: PanelContent = {
          type: 'file',
          filePath: file.url || file.downloadUrl || 'Unknown path',
          fileName: file.name,
          content: fileContent.content,
          language: fileContent.language,
        }

        // Create breadcrumbs
        const breadcrumbs: BreadcrumbItem[] = [
          ...parentBreadcrumbs,
          {
            label: file.name,
            id: file.id || file.name,
            clickable: false, // Current item is not clickable
          },
        ]

        // Open in panel
        onOpenPanel(panelContent, breadcrumbs)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to open file'
        setError(errorMessage)
        console.error('Error opening file in panel:', error)
      } finally {
        setLoading(false)
      }
    },
    [onOpenPanel, parentBreadcrumbs]
  )

  return {
    openFileInPanel,
    loading,
    error,
    clearError: () => setError(null),
  }
}

export type { FileData }
